import { useEffect, useState } from "react";
import { api, fmtDateTime, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { Send, Pause, Play, Eye, Upload, Users, MousePointerClick, Trash2 } from "lucide-react";
import { NewsletterSelection } from "../../components/admin/NewsletterSelection";

const STATUS_LABEL = { no_subscribers: "Aucun abonné actif", sent: "Envoyée", partial: "Partielle", failed: "Échec", paused: "En pause (non envoyée)", sending: "En cours" };
const SUB_LABEL = { active: "Actif", pending: "En attente", unsubscribed: "Désinscrit" };

const Card = ({ title, children, testId, right }) => (
  <section data-testid={testId} className="bg-white border border-[var(--line)] rounded-md p-6">
    <div className="flex items-center justify-between mb-4 gap-3">
      <h2 className="font-display text-xl">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Stat = ({ label, value, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-white border border-[var(--line)] rounded-md p-5">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-neutral-500">
      {label} <Icon className="h-4 w-4 text-[var(--red)]" />
    </div>
    <div className="font-display text-4xl mt-3">{value ?? "—"}</div>
  </div>
);

const SponsorForm = ({ sponsor, onChange }) => {
  const set = (k) => (e) => onChange({ ...sponsor, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const t = toast.loading("Envoi de l'image…");
    try {
      const { data } = await api.post("/media/upload", fd);
      onChange({ ...sponsor, image_url: data.url });
      toast.success("Image ajoutée", { id: t });
    } catch {
      toast.error("Échec de l'envoi", { id: t });
    }
  };
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" checked={sponsor.enabled} onChange={set("enabled")} data-testid="nl-sponsor-enabled" className="h-4 w-4 accent-[var(--red)]" />
        Afficher l'encart « Sponsorisé » dans la prochaine newsletter
      </label>
      <Field label="Titre"><input className="input" value={sponsor.title} onChange={set("title")} data-testid="nl-sponsor-title" /></Field>
      <Field label="Texte"><textarea className="input min-h-[70px]" value={sponsor.text} onChange={set("text")} data-testid="nl-sponsor-text" /></Field>
      <div className="grid sm:grid-cols-[1fr_160px] gap-3">
        <Field label="Lien (https://…)"><input className="input" value={sponsor.link_url} onChange={set("link_url")} data-testid="nl-sponsor-link" /></Field>
        <Field label="Bouton"><input className="input" value={sponsor.cta} onChange={set("cta")} data-testid="nl-sponsor-cta" /></Field>
      </div>
      <Field label="Image (bannière 1200×600 conseillée)">
        <div className="flex items-center gap-3">
          {sponsor.image_url && <img src={mediaUrl(sponsor.image_url)} alt="" className="h-14 w-24 object-cover rounded-sm border border-[var(--line)]" />}
          <label className="btn-ghost cursor-pointer">
            <Upload className="h-4 w-4" /> Choisir
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="nl-sponsor-image-input" />
          </label>
          {sponsor.image_url && (
            <button type="button" className="text-xs text-neutral-500 hover:text-[var(--red)]" onClick={() => onChange({ ...sponsor, image_url: "" })}>
              Retirer
            </button>
          )}
        </div>
      </Field>
    </div>
  );
};

export const AdminNewsletter = () => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPreview = () => api.get("/admin/newsletter/preview").then((r) => setPreviewHtml(r.data));
  const load = () => api.get("/admin/newsletter").then((r) => { setData(r.data); setSettings(r.data.settings); });

  useEffect(() => { load(); loadPreview(); }, []);

  const save = async (next = settings) => {
    setBusy(true);
    try {
      const { data: s } = await api.put("/admin/newsletter/settings", next);
      setSettings(s);
      toast.success("Réglages enregistrés");
      loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const togglePause = () => save({ ...settings, paused: !settings.paused });

  const sendTest = async () => {
    const t = toast.loading("Envoi du test…");
    try {
      const { data: r } = await api.post("/admin/newsletter/test", {});
      toast.success(`Test envoyé à ${r.to}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible", { id: t });
    }
  };

  const sendNow = async () => {
    if (!window.confirm(`Envoyer maintenant la newsletter à ${data.counts.active} abonné(s) ? (une seule fois par semaine)`)) return;
    await api.post("/admin/newsletter/send-now");
    toast.success("Envoi lancé en tâche de fond");
    setTimeout(load, 4000);
  };

  const removeSub = async (email) => {
    if (!window.confirm(`Supprimer définitivement ${email} ?`)) return;
    await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
    load();
  };

  if (!data || !settings) return <div className="text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="admin-newsletter-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Diffusion</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Newsletter</h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Envoi automatique chaque lundi à 8h (heure de Paris) · Prochaine édition : {data.next_week.label}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={togglePause} disabled={busy} data-testid="nl-pause-toggle" className={settings.paused ? "btn-red" : "btn-ghost"}>
            {settings.paused ? <><Play className="h-4 w-4" /> Reprendre les envois</> : <><Pause className="h-4 w-4" /> Suspendre les envois</>}
          </button>
          <button onClick={sendTest} data-testid="nl-test-btn" className="btn-ghost"><Send className="h-4 w-4" /> M'envoyer un test</button>
          <button onClick={sendNow} data-testid="nl-send-now-btn" className="btn-ghost">Envoyer maintenant</button>
        </div>
      </div>

      {settings.paused && (
        <div data-testid="nl-paused-banner" className="border border-[var(--red)] bg-[var(--red-soft)] text-[var(--red)] rounded-md px-4 py-3 text-sm">
          Les envois sont suspendus : aucune newsletter ne partira lundi tant que vous ne les reprenez pas.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Abonnés actifs" value={data.counts.active} icon={Users} testId="nl-count-active" />
        <Stat label="En attente" value={data.counts.pending} icon={Users} testId="nl-count-pending" />
        <Stat label="Désinscrits" value={data.counts.unsubscribed} icon={Users} testId="nl-count-unsub" />
        <Stat label="Clics sponsor" value={data.sponsor_clicks} icon={MousePointerClick} testId="nl-sponsor-clicks" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_640px] gap-6 items-start">
        <div className="space-y-6">
          <NewsletterSelection onChange={loadPreview} />
          <Card title="Encart sponsorisé" testId="nl-sponsor-card"
            right={<button onClick={() => save()} disabled={busy} data-testid="nl-save-btn" className="btn-red">Enregistrer</button>}>
            <SponsorForm sponsor={settings.sponsor} onChange={(sponsor) => setSettings({ ...settings, sponsor })} />
          </Card>
          <Card title="Derniers envois" testId="nl-sends-card">
            {data.sends.length === 0 ? (
              <div className="text-sm text-neutral-500">Aucun envoi pour l'instant.</div>
            ) : (
              <ul className="divide-y divide-[var(--line)] text-sm">
                {data.sends.map((s, i) => (
                  <li key={i} className="py-2.5 flex justify-between gap-3" data-testid={`nl-send-row-${i}`}>
                    <span>Semaine du {s.week_start} · <span className="text-neutral-500">{s.trigger === "cron" ? "auto" : "manuel"}</span></span>
                    <span className={s.status === "sent" ? "text-neutral-800" : "text-[var(--red)]"}>
                      {STATUS_LABEL[s.status] || s.status}{s.sent != null ? ` · ${s.sent} envoyés` : ""} · {fmtDateTime(s.started_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Inscrits récents" testId="nl-subs-card">
            {data.subscribers.length === 0 ? (
              <div className="text-sm text-neutral-500">Aucun inscrit pour l'instant.</div>
            ) : (
              <ul className="divide-y divide-[var(--line)] text-sm">
                {data.subscribers.map((s) => (
                  <li key={s.email} className="py-2 flex items-center justify-between gap-3">
                    <span className="truncate">{s.email}</span>
                    <span className="flex items-center gap-3 text-xs text-neutral-500 shrink-0">
                      {SUB_LABEL[s.status]} · {fmtDateTime(s.created_at)}
                      <button onClick={() => removeSub(s.email)} aria-label="Supprimer" data-testid={`nl-sub-delete-${s.email}`} className="hover:text-[var(--red)]">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        <Card title="Aperçu de la prochaine édition" testId="nl-preview-card"
          right={<button onClick={loadPreview} className="text-xs uppercase tracking-[0.2em] text-[var(--red)] inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Rafraîchir</button>}>
          <iframe title="Aperçu newsletter" srcDoc={previewHtml} data-testid="nl-preview-iframe" className="w-full h-[900px] border border-[var(--line)] rounded-sm bg-[#f4f1ec]" />
        </Card>
      </div>
    </div>
  );
};
